import { Box, Divider, Typography } from '@mui/material';
import CustomAutocomplete from 'components/shared/CustomAutocomplete/CustomAutocomplete';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomDrawer from 'components/shared/CustomDrawer/CustomDrawer';
import CustomTextField from 'components/shared/CustomTextField/CustomTextField';
import { useAlertStatus } from 'customHook/useAlertStatus';
import { useImagePreview } from 'customHook/useProfilePicture';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ContactService } from 'service/contact.service';
import { fetchContactDropdowns } from 'store/slices/contactSlice';
import zeplinColor from 'theme/Color';

const AddContacts = ({
  drawerState,
  setDrawerState,
  onSuccess,
  selectedData,
}: {
  drawerState: boolean;
  setDrawerState: (state: boolean) => void;
  onSuccess?: () => void;
  selectedData?: any;
}) => {
  const dispatch = useDispatch<any>();
  const contactsState = useSelector((state: any) => state.contacts);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const { alertState, startOperation, completeOperation, getAlertContent, setAlertState } =
    useAlertStatus();

  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [selectedItems, setSelectedItems] = useState<{
    client: { Name: string; Id: string }[];
    manager: { Name: string; Id: string }[];
    company: { Name: string; Id: string }[];
  }>({
    client: [],
    manager: [],
    company: [],
  });
  const {
    fileInputRef,
    previewUrl: avatarPreview,
    handleFileChange,
    clearSelection,
    openFileSelection,
    uploadProfilePicture,
  } = useImagePreview(null);
  const handleValueChange =
    (key: string) =>
    (event: React.ChangeEvent<HTMLInputElement> | { target: { value: string } }) => {
      setFormValues((prev) => ({ ...prev, [key]: event.target.value }));
    };
  const onClose = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerState(false);
  };
  const handleAssignSelection = (
    key: 'client' | 'manager' | 'company',
    value: { Name: string; Id: string } | null,
  ) => {
    setSelectedItems((prev) => ({ ...prev, [key]: value ? [value] : [] }));
  };

  const handleAddContact = async () => {
    try {
      setIsSubmitting(true);
      startOperation('create', 'Adding contact');
      let uploadedUrl: string | null = null;
      if (avatarPreview) {
        const result = await uploadProfilePicture({ key: false, type: 'profile' });
        uploadedUrl = result === undefined ? null : result;
      }

      if (selectedData) {
        await ContactService.updateContact(selectedData.contactId, {
          name: formValues.name || '',
          email: formValues.email || '',
          phone: formValues.phone || '',
          role: formValues.role || '',
          note: formValues.note || '',
          clientId: selectedItems.client?.[0]?.Id,
          managerId: selectedItems.manager?.[0]?.Id,
          company: selectedItems.company?.[0]?.Name || formValues.company || '',
          profileImageUrl: uploadedUrl ?? '',
        });
        completeOperation('update', 'Contact updated successfully');
      } else {
        console.log('Adding contact with values:', uploadedUrl);
        await ContactService.addContact({
          name: formValues.name || '',
          email: formValues.email || '',
          phone: formValues.phone || '',
          role: formValues.role || '',
          note: formValues.note || '',
          clientId: selectedItems.client?.[0]?.Id,
          managerId: selectedItems.manager?.[0]?.Id,
          company: selectedItems.company?.[0]?.Name || formValues.company || '',
          profileImageUrl: uploadedUrl ?? '',
        });
        completeOperation('create', 'Contact added successfully');
      }

      setDrawerState(false);
      onSuccess?.();
    } catch (error: any) {
      completeOperation('create', error?.response?.data?.message || 'Failed to add contact', true);
    } finally {
      setIsSubmitting(false);
    }
  };
  useEffect(() => {
    if (drawerState) {
      dispatch(fetchContactDropdowns());
    }
  }, [drawerState, dispatch]);
  const options = contactsState?.companyOptions || [];

  useEffect(() => {
    if (!drawerState || !selectedData) return;

    setFormValues({
      name: selectedData?.name || '',
      email: selectedData?.email || '',
      phone: selectedData?.phone || '',
      company: selectedData?.company || '',
      role: selectedData?.role || '',
      note: selectedData?.note || '',
    });

    const findById = (list: any[] = [], id?: string) =>
      list.find((item) => String(item?.Id || item?.id) === String(id));

    const findByCompany = (list: any[] = [], company?: string) =>
      list.find(
        (item) =>
          String(item?.Name || item?.name || '').toLowerCase() ===
          String(company || '').toLowerCase(),
      );

    const clientMatch = findById(
      options?.assignTo || [],
      selectedData?.clientId || selectedData?.client?.Id || selectedData?.client?.id,
    );

    const managerMatch = findById(
      options?.relatedTo?.account || [],
      selectedData?.managerId || selectedData?.manager?.Id || selectedData?.manager?.id,
    );

    const companyMatch =
      findById(
        options?.zoomInfoTargets || [],
        selectedData?.companyId || selectedData?.company?.Id || selectedData?.company?.id,
      ) || findByCompany(options?.zoomInfoTargets || [], selectedData?.company);

    setSelectedItems({
      client: clientMatch ? [clientMatch] : [],
      manager: managerMatch ? [managerMatch] : [],
      company: companyMatch ? [companyMatch] : [],
    });
  }, [drawerState, selectedData, options]);

  return (
    <CustomDrawer state={drawerState} onClose={onClose(false)}>
      <Box sx={{ width: { xs: '427px', xl: '527px' }, position: 'relative', overflowY: 'auto' }}>
        <Box
          sx={{
            zIndex: 1000,
            backgroundColor: zeplinColor.Neutral1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: { xs: '24px 24px 24px 24px', xl: '40px 24px 24px 24px' },
            }}
          >
            <Typography variant='responsiveHeading2'>
              {selectedData ? 'Edit Contact' : 'New Contact'}
            </Typography>
            <img
              src='/assets/close-icon.svg'
              style={{ width: '24px', height: '24px', cursor: 'pointer' }}
              alt='close'
              onClick={onClose(false)}
            />
          </Box>
          <Divider sx={{ borderColor: zeplinColor.Neutral5 }} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100vh - 100px)',
            overflowY: 'auto',
          }}
        >
          <Box
            sx={{
              top: 0,
              backgroundColor: zeplinColor.Neutral1,
              zIndex: 10,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', padding: '24px' }}>
              <Box
                sx={{
                  display: 'flex',

                  gap: '16px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '80px',
                    height: '80px',
                    borderRadius: '22px',
                    border: !avatarPreview ? `1px dashed ${zeplinColor.Neutral9}` : '',
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={avatarPreview ?? '/assets/account-name.svg'}
                    alt='profile icon'
                    width={avatarPreview ? 80 : 35}
                    height={avatarPreview ? 80 : 35}
                    style={{ objectFit: 'cover' }}
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    flexDirection: 'column',
                    gap: '12px',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Typography variant='body2' sx={{ color: zeplinColor.BasicBlack }}>
                      Profile photo
                    </Typography>
                    <Typography variant='label1' sx={{ color: zeplinColor.Neutral8 }}>
                      JPG or PNG (max 2 MB)
                    </Typography>
                  </Box>
                  <Box>
                    <CustomButton
                      disabled={false}
                      onClick={openFileSelection}
                      variant='contained'
                      color={'primary'}
                      buttonVariant='buttonSmall'
                      style={{
                        borderRadius: { xs: '10px', xl: '14px' },
                        padding: '6px 12px',
                        '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                      }}
                      textStyle={{ color: zeplinColor.Neutral1 }}
                      text={avatarPreview ? 'Change Photo' : 'Add Photo'}
                    />
                    <input
                      type='file'
                      multiple
                      accept='.jpeg, .jpg, .png, image/jpeg, image/png'
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                    {avatarPreview && (
                      <CustomButton
                        disabled={false}
                        variant='contained'
                        buttonVariant='buttonSmall'
                        onClick={clearSelection}
                        text={'Delete Photo'}
                        textStyle={{
                          color: zeplinColor.Error,
                        }}
                        style={{
                          backgroundColor: 'transparent',
                          borderRadius: { xs: '10px', xl: '14px' },
                          marginLeft: '8px',
                          padding: '6px 12px',
                          '&.Mui-focusVisible, &:hover': { backgroundColor: zeplinColor.Neutral3 },
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
              <CustomTextField
                name='Name'
                text={formValues.name || ''}
                form={true}
                label={'Name'}
                type='text'
                onChange={handleValueChange('name')}
                required
              />
              <Box sx={{ flex: 1 }}>
                <CustomAutocomplete
                  required={true}
                  options={options?.assignTo || []}
                  getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
                  label={'Client name'}
                  handleDeleteItems={() => {}}
                  selectedItems={selectedItems}
                  handleSelection={(value: any) => handleAssignSelection('client', value)}
                  value={selectedItems.client[0]}
                  handleFilter={() => {}}
                  placeholder='Client name'
                  currentLength={options?.assignTo?.length}
                  totalLength={options?.assignTo?.length}
                  next={() => {}}
                  multiple={false}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <CustomAutocomplete
                  required={true}
                  options={options?.relatedTo?.account || []}
                  getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
                  label={'Manager'}
                  handleDeleteItems={() => {}}
                  selectedItems={selectedItems}
                  handleSelection={(value: any) => handleAssignSelection('manager', value)}
                  value={selectedItems.manager[0]}
                  handleFilter={() => {}}
                  placeholder='Manager'
                  currentLength={options?.relatedTo?.account?.length}
                  totalLength={options?.relatedTo?.account?.length}
                  next={() => {}}
                  multiple={false}
                />
              </Box>
              <Box sx={{ flex: 1 }}>
                <CustomAutocomplete
                  options={options?.zoomInfoTargets || []}
                  getOptionLabel={(option: any) => (option?.Name ? option?.Name : '')}
                  label={'Company'}
                  handleDeleteItems={() => {}}
                  selectedItems={selectedItems}
                  handleSelection={(value: any) => handleAssignSelection('company', value)}
                  value={selectedItems.company[0]}
                  handleFilter={() => {}}
                  placeholder='Company'
                  currentLength={options?.zoomInfoTargets?.length}
                  totalLength={options?.zoomInfoTargets?.length}
                  next={() => {}}
                  multiple={false}
                />
              </Box>
              <CustomTextField
                name='Role'
                text={formValues.role || ''}
                form={true}
                label={'Role'}
                type='text'
                onChange={handleValueChange('role')}
              />
              <CustomTextField
                name='Email'
                text={formValues.email || ''}
                form={true}
                label={'Email'}
                type='text'
                onChange={handleValueChange('email')}
              />
              <CustomTextField
                style={{ flex: 1 }}
                name='Phone number'
                text={formValues.phone || ''}
                form={true}
                label={'Phone number'}
                type='text'
                isPhoneMask
                onChange={handleValueChange('phone')}
              />
              <CustomTextField
                name='Add a note'
                text={formValues.note || ''}
                form={true}
                label={'Add a note'}
                type='text'
                onChange={handleValueChange('note')}
                multiline
                rows={3}
              />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            width: '100%',
            position: 'sticky',
            bottom: '0px',
            margin: '0px',
            zIndex: '100',
            padding: '24px',
            backgroundColor: zeplinColor.Neutral1,
            display: 'flex',
            gap: '16px',
          }}
        >
          <CustomButton
            disabled={
              isSubmitting || formValues.name === '' || selectedItems.client.length === 0
                ? true
                : false
            }
            onClick={handleAddContact}
            variant={'contained'}
            color={'primary'}
            style={{
              borderRadius: '14px',
              height: 56,
              width: '100%',
              border: 'none',
              backgroundColor: zeplinColor.Brand1,
            }}
            textStyle={{
              color: zeplinColor.Neutral1,
            }}
            text={
              isSubmitting
                ? selectedData
                  ? 'Updating...'
                  : 'Adding...'
                : selectedData
                  ? 'Update Contact'
                  : 'Add Contact'
            }
          />
        </Box>
      </Box>
    </CustomDrawer>
  );
};

export default AddContacts;
